<template>
  <!-- Teleport the drawer to the body -->
  <Teleport to="body">
    <div>
      <!-- Overlay background with blur -->
      <div
        v-if="isOpen"
        :class="[
          priority ? 'z-[60]' : 'z-40',
          withBlur ? 'opaque fixed inset-0 backdrop-blur-sm' : 'fixed inset-0',
        ]"
        :style="backdropStyle"
        @click="closeDrawer"></div>

      <!-- Drawer content with transition -->
      <transition :name="transitionName">
        <div
          v-if="isOpen"
          ref="sheetRef"
          :class="[
            'fixed flex max-h-full flex-col rounded-t-gc-sm border border-b-0 border-border-pop bg-bg-pop shadow-lg md:min-w-[390px] md:max-w-[530px] md:rounded-l-gc-sm md:rounded-tr-none md:border-b md:border-r-0',
            priority ? 'z-[70]' : 'z-50',
            positionClasses,
            drawerStyles,
            mainContainerClass,
            isBottomSheet ? 'drawer-safe-area-bottom' : '',
          ]"
          :style="sheetDragStyle">
          <!-- Content Wrapper -->
          <div
            class="drawerMainContainer flex h-full max-h-full flex-col overflow-y-hidden overscroll-contain">
            <!-- Drag handle -->
            <div
              v-if="position === 'bottom' && draggable"
              :role="isDragEnabled ? 'button' : undefined"
              :tabindex="isDragEnabled ? 0 : undefined"
              :aria-label="isDragEnabled ? 'Drag to close' : undefined"
              :class="[
                'drawer-drag-handle flex w-full justify-center py-6',
                isDragEnabled
                  ? 'cursor-grab touch-none select-none active:cursor-grabbing'
                  : '',
              ]"
              @pointerdown="onHandlePointerDown"
              @keydown.enter.prevent="isDragEnabled && closeDrawer()"
              @keydown.space.prevent="isDragEnabled && closeDrawer()">
              <div
                class="pointer-events-none h-1 w-1/3 rounded-md bg-dark"></div>
            </div>
            <div
              v-else-if="position === 'bottom' && !draggable"
              class="pointer-events-none shrink-0 py-6"
              aria-hidden="true">
              <div class="h-1" />
            </div>

            <!-- Slot for drawer content -->
            <div class="font-secondary" :class="drawerContentClass">
              <slot name="drawerContent">Default drawer content</slot>
            </div>
          </div>
          <!-- Slot for drawer buttons -->
          <div v-if="$slots.drawerButtons">
            <hr />
            <div class="mx-8 my-5">
              <slot name="drawerButtons">Default drawer buttons</slot>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useDrawerDrag } from '@composables/useDrawerDrag';
import {
  lockDrawerScroll,
  unlockDrawerScroll,
} from '@composables/drawerScrollLock';
import { useScreenSize } from '@composables/useScreenSize';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  position: {
    type: String,
    default: 'right',
    validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value),
  },
  width: {
    type: String,
    default: 'w-[300px]',
  },
  height: {
    type: String,
    default: 'h-[300px]',
  },
  drawerContentClass: {
    type: String,
    default:
      'drawerContentContainer flex flex-col flex-grow max-h-full overflow-y-hidden mx-4 md:mx-10p',
  },
  withBlur: {
    type: Boolean,
    default: true,
  },
  mainContainerClass: {
    type: String,
    default: '',
  },
  priority: {
    type: Boolean,
    default: false,
  },
  draggable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const sheetRef = ref(null);
const { isMobile, setupScreenSizeListeners } = useScreenSize();
setupScreenSizeListeners();

const isOpen = computed(() => props.modelValue);
const isBottomSheet = computed(() => props.position === 'bottom');
const isDragEnabled = computed(
  () =>
    isBottomSheet.value && props.draggable && isMobile.value && isOpen.value,
);

const closeDrawer = () => {
  emit('update:modelValue', false);
};

const { dragOffset, dragProgress, onHandlePointerDown, resetDrag } =
  useDrawerDrag({
    sheetRef,
    enabled: isDragEnabled,
    onDismiss: closeDrawer,
  });

const sheetDragStyle = computed(() => {
  if (!isBottomSheet.value || dragOffset.value <= 0) {
    return {};
  }

  return {
    transform: `translateY(${dragOffset.value}px)`,
    transition: 'none',
    willChange: 'transform',
  };
});

const backdropStyle = computed(() => {
  if (!isBottomSheet.value || dragProgress.value <= 0) {
    return {};
  }

  return {
    opacity: String(1 - dragProgress.value),
  };
});

watch(
  () => isOpen.value,
  (value) => {
    if (value) {
      resetDrag();
      lockDrawerScroll();
    } else {
      resetDrag();
      unlockDrawerScroll();
    }
  },
);

const onEscapeKeydown = (event) => {
  if (event.key === 'Escape' && isOpen.value) {
    closeDrawer();
  }
};

onMounted(() => {
  window.addEventListener('keydown', onEscapeKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onEscapeKeydown);
  if (isOpen.value) {
    unlockDrawerScroll();
  }
});

// CSS classes based on drawer position
const positionClasses = computed(() => {
  switch (props.position) {
    case 'top':
      return `top-0 left-0 right-0`;
    case 'bottom':
      return `bottom-0 left-0 right-0`;
    case 'left':
      return `top-0 left-0 bottom-0 md:rounded-l-none md:rounded-br-gc-sm md:rounded-tr-gc-sm md:border-l-0 md:!border-r`;
    case 'right':
      return `top-0 right-0 bottom-0`;
    default:
      return '';
  }
});

// Styles for drawer dimensions (width/height)
const drawerStyles = computed(() => {
  if (['left', 'right'].includes(props.position)) {
    return props.width || 'w-full';
  } else if (['top', 'bottom'].includes(props.position)) {
    return props.height || 'h-full';
  }
  return '';
});

// Define the transition name based on position
const transitionName = computed(() => {
  switch (props.position) {
    case 'top':
      return 'slide-down';
    case 'bottom':
      return 'slide-up';
    case 'left':
      return 'slide-left';
    case 'right':
      return 'slide-right';
    default:
      return '';
  }
});
</script>

<style scoped>
/* Transitions for drawer sliding based on position */
.slide-down-enter-active,
.slide-up-enter-active,
.slide-right-enter-active,
.slide-left-enter-active {
  transition: transform 0.5s ease-in;
}

.slide-down-leave-active,
.slide-up-leave-active,
.slide-right-leave-active,
.slide-left-leave-active {
  transition: transform 0.5s ease-out;
}
.slide-left-enter-from,
.slide-left-leave-to {
  transform: translateX(-100%);
}
.slide-right-enter-from,
.slide-right-leave-to {
  transform: translateX(100%);
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}
.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
}
.opaque {
  @apply bg-overlay;
}

.drawer-drag-handle {
  -webkit-touch-callout: none;
}

.drawer-safe-area-bottom {
  padding-bottom: env(safe-area-inset-bottom);
}
</style>
<style>
.no-scroll {
  @apply overflow-hidden;
}
</style>
